import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AppSelector from './App';
import reportWebVitals from './reportWebVitals';
import LoginPage from './login/login';
import AdminDashboard from './admin/AdminDashboard';
import AnalyticsDashboard from './admin/AnalyticsDashboard';
import { AdminI18nProvider } from './admin/i18n';
import config from './config';
import { handleSessionExpired } from './utils/sessionExpiry';

// When any authenticated API call returns 401/403 the session/cookie has expired.
// Show the "session terminated" popup and log the user out.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      handleSessionExpired();
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const isLocalDevelopment = process.env.NODE_ENV === 'development';
  // Initialize ready state: true for production (handled by redirect), false for dev (awaits setup)
  const [isReady, setIsReady] = React.useState(!isLocalDevelopment);

  useEffect(() => {
    // Set mock userinfo for local development
    if (isLocalDevelopment) {
      if (!localStorage.getItem('userinfo')) {
        const devConfig = config.development;
        localStorage.setItem('userinfo', devConfig.userEmail);
        sessionStorage.setItem('authCompleted', 'true');
        sessionStorage.setItem('userinfoResponse', JSON.stringify({
          "user": devConfig.userId,
          "email": devConfig.emailId,
          "preferredUsername": devConfig.preferredUsername
        }));
      }
      setIsReady(true);
    }
  }, [isLocalDevelopment]);

  // Check authentication status
  // In production, this is set after successful OAuth2 callback
  // In development, this is set automatically above
  const isAuthenticated = sessionStorage.getItem('authCompleted');

  // Wait for dev setup
  if (!isReady) return null;

  // Redirect to login if not authenticated (except in local development)
  if (!isAuthenticated && !isLocalDevelopment) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Component to handle root path - clears session and redirects to login
const RootRedirect = () => {
  useEffect(() => {
    // Clear ALL session storage
    sessionStorage.clear();
    localStorage.clear();
    window.location.replace('/login');
  }, []);

  return null;
};

// Component to handle invalid route access
const RedirectToLogin = () => {
  useEffect(() => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.replace('/login');
  }, []);

  return null;
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/dtc-bot" element={
          <ProtectedRoute>
            <AppSelector />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminI18nProvider>
              <AdminDashboard />
            </AdminI18nProvider>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <AdminI18nProvider>
              <AnalyticsDashboard />
            </AdminI18nProvider>
          </ProtectedRoute>
        } />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<RootRedirect />} />
        {/* Catch all invalid routes and redirect to login */}
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    </Router>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();